/**
 * Bill Parser Service
 * Parses uploaded bills/receipts using AI vision and extracts expense data
 */

import type {
  ParsedBillData,
  ParsedLineItem,
  MatchedBillData,
  UnmatchedSuggestion,
  SuggestionItem,
} from '../types/billParser';

// API Configuration - Use environment variable or fallback
const GEMINI_API_KEY = import.meta.env.VITE_GEMINI_API_KEY || '';
const GEMINI_API_URL = 'https://generativelanguage.googleapis.com/v1beta/models/gemini-flash-latest:generateContent';

/**
 * Convert file to base64 for API submission
 */
export async function fileToBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => {
      const result = reader.result as string;
      // Remove the data URL prefix (e.g., "data:image/png;base64,")
      const base64 = result.split(',')[1];
      resolve(base64);
    };
    reader.onerror = (error) => reject(error);
  });
}

/**
 * Get MIME type from file
 */
export function getMimeType(file: File): string {
  return file.type || 'application/octet-stream';
}

/**
 * Parse a bill image using Gemini Vision API
 */
export async function parseBillWithAI(file: File): Promise<ParsedBillData> {
  if (!GEMINI_API_KEY) {
    throw new Error('Gemini API key not configured. Please set VITE_GEMINI_API_KEY in your environment.');
  }

  const base64Image = await fileToBase64(file);
  const mimeType = getMimeType(file);

  const prompt = `Analyze this bill/receipt/invoice image and extract the following information in JSON format. Be thorough and extract all visible data.

Return a JSON object with these fields:
{
  "vendorName": "Name of the shop/vendor/business",
  "vendorAddress": "Full address if visible",
  "vendorPhone": "Phone number if visible",
  "vendorGST": "GST/Tax ID number if visible (e.g., GSTIN in India)",
  "invoiceNumber": "Invoice/Bill number",
  "invoiceDate": "Date in YYYY-MM-DD format",
  "dueDate": "Due date if visible in YYYY-MM-DD format",
  "lineItems": [
    {
      "description": "Item description",
      "quantity": number or null,
      "rate": unit price as number or null,
      "amount": total amount for this line as number,
      "unit": "unit of measurement like kg, pcs, bags, etc."
    }
  ],
  "subtotal": number or null,
  "taxAmount": total tax amount as number or null,
  "gstAmount": GST amount specifically as number or null,
  "gstPercent": GST percentage as number or null,
  "discount": discount amount as number or null,
  "totalAmount": final total as number,
  "paymentMethod": "cash/card/upi/credit etc if visible",
  "notes": "any other relevant notes"
}

Important:
- Extract ALL line items from the bill
- Amounts should be numbers, not strings
- Dates should be in YYYY-MM-DD format
- If a field is not visible or not applicable, set it to null
- For Indian bills, look for GSTIN numbers (15 alphanumeric characters)
- Parse both English and regional language text if present

Return ONLY the JSON object, no other text.`;

  const requestBody = {
    contents: [
      {
        parts: [
          {
            inlineData: {
              mimeType,
              data: base64Image,
            },
          },
          {
            text: prompt,
          },
        ],
      },
    ],
    generationConfig: {
      temperature: 0.1,
      topK: 32,
      topP: 1,
      maxOutputTokens: 4096,
    },
  };

  const response = await fetch(`${GEMINI_API_URL}?key=${GEMINI_API_KEY}`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(requestBody),
  });

  if (!response.ok) {
    const errorText = await response.text();
    console.error('Gemini API error:', errorText);
    throw new Error(`Failed to parse bill: ${response.status} ${response.statusText}`);
  }

  const result = await response.json();

  // Extract the text content from Gemini response
  const textContent = result.candidates?.[0]?.content?.parts?.[0]?.text;

  if (!textContent) {
    throw new Error('No response from AI model');
  }

  // Parse the JSON from the response
  try {
    // Clean up the response - remove markdown code blocks if present
    let jsonString = textContent.trim();
    if (jsonString.startsWith('```json')) {
      jsonString = jsonString.slice(7);
    }
    if (jsonString.startsWith('```')) {
      jsonString = jsonString.slice(3);
    }
    if (jsonString.endsWith('```')) {
      jsonString = jsonString.slice(0, -3);
    }
    jsonString = jsonString.trim();

    const parsedData: ParsedBillData = JSON.parse(jsonString);

    // Ensure lineItems is always an array
    if (!Array.isArray(parsedData.lineItems)) {
      parsedData.lineItems = [];
    }

    // Calculate confidence based on how many fields were extracted
    const fields = [
      parsedData.vendorName,
      parsedData.invoiceDate,
      parsedData.totalAmount,
      parsedData.lineItems.length > 0,
    ];
    const filledFields = fields.filter(Boolean).length;
    parsedData.confidence = filledFields / fields.length;

    // Store raw text for debugging
    parsedData.rawText = textContent;

    return parsedData;
  } catch (parseError) {
    console.error('Failed to parse AI response:', textContent);
    throw new Error('Failed to parse bill data from AI response');
  }
}

/**
 * Calculate string similarity using Levenshtein distance
 */
function calculateSimilarity(str1: string, str2: string): number {
  const s1 = str1.toLowerCase().trim();
  const s2 = str2.toLowerCase().trim();

  if (s1 === s2) return 1;
  if (s1.length === 0 || s2.length === 0) return 0;

  // Check if one contains the other
  if (s1.includes(s2) || s2.includes(s1)) {
    return 0.8;
  }

  // Levenshtein distance
  const matrix: number[][] = [];
  for (let i = 0; i <= s1.length; i++) {
    matrix[i] = [i];
  }
  for (let j = 0; j <= s2.length; j++) {
    matrix[0][j] = j;
  }

  for (let i = 1; i <= s1.length; i++) {
    for (let j = 1; j <= s2.length; j++) {
      if (s1[i - 1] === s2[j - 1]) {
        matrix[i][j] = matrix[i - 1][j - 1];
      } else {
        matrix[i][j] = Math.min(
          matrix[i - 1][j - 1] + 1,
          matrix[i][j - 1] + 1,
          matrix[i - 1][j] + 1
        );
      }
    }
  }

  const distance = matrix[s1.length][s2.length];
  const maxLength = Math.max(s1.length, s2.length);
  return 1 - distance / maxLength;
}

/**
 * Find best matches for a value from a list of entities
 */
function findBestMatches(
  searchValue: string,
  entities: { id: string; name: string }[],
  topN: number = 5
): SuggestionItem[] {
  if (!searchValue || entities.length === 0) return [];

  const scored = entities.map((entity) => ({
    id: entity.id,
    name: entity.name,
    matchScore: calculateSimilarity(searchValue, entity.name),
  }));

  return scored
    .filter((item) => item.matchScore > 0.3)
    .sort((a, b) => b.matchScore - a.matchScore)
    .slice(0, topN);
}

/**
 * Infer expense nature from vendor name or line items
 */
function inferNature(
  parsedData: ParsedBillData
): 'routine' | 'stock' | 'payroll' | 'admin-finance' | 'capital' | undefined {
  const vendorLower = (parsedData.vendorName || '').toLowerCase();
  const itemDescriptions = parsedData.lineItems
    .map((item) => item.description.toLowerCase())
    .join(' ');

  // Stock-related keywords
  const stockKeywords = [
    'supplier', 'wholesale', 'bulk', 'material', 'raw', 'inventory',
    'cotton', 'fiber', 'grain', 'rice', 'paddy', 'wheat'
  ];
  if (stockKeywords.some((kw) => vendorLower.includes(kw) || itemDescriptions.includes(kw))) {
    return 'stock';
  }

  // Payroll-related keywords
  const payrollKeywords = ['salary', 'wage', 'labour', 'labor', 'worker', 'staff'];
  if (payrollKeywords.some((kw) => vendorLower.includes(kw) || itemDescriptions.includes(kw))) {
    return 'payroll';
  }

  // Admin/Finance keywords
  const adminKeywords = [
    'bank', 'insurance', 'tax', 'audit', 'legal', 'consulting',
    'office supplies', 'stationery', 'electricity', 'water', 'internet'
  ];
  if (adminKeywords.some((kw) => vendorLower.includes(kw) || itemDescriptions.includes(kw))) {
    return 'admin-finance';
  }

  // Capital keywords
  const capitalKeywords = [
    'machinery', 'equipment', 'vehicle', 'land', 'building',
    'construction', 'renovation', 'furniture'
  ];
  if (capitalKeywords.some((kw) => vendorLower.includes(kw) || itemDescriptions.includes(kw))) {
    return 'capital';
  }

  // Default to routine
  return 'routine';
}

/**
 * Match parsed bill data with existing app entities
 */
export function matchBillDataWithEntities(
  parsedData: ParsedBillData,
  entities: {
    types: { id: string; name: string }[];
    units: { id: string; name: string; typeId?: string }[];
    costCenters: { id: string; name: string; typeId?: string }[];
    categories: { id: string; name: string; costCenterId?: string }[];
    parties: { id: string; name: string; type: string }[];
  }
): MatchedBillData {
  const unmatchedSuggestions: UnmatchedSuggestion[] = [];

  // Try to match vendor/party
  let partyMatch: { id?: string; name?: string; type?: string; confidence?: number } = {};
  if (parsedData.vendorName) {
    const partyMatches = findBestMatches(parsedData.vendorName, entities.parties);
    if (partyMatches.length > 0 && partyMatches[0].matchScore > 0.6) {
      const matchedParty = entities.parties.find((p) => p.id === partyMatches[0].id);
      partyMatch = {
        id: partyMatches[0].id,
        name: partyMatches[0].name,
        type: matchedParty?.type,
        confidence: partyMatches[0].matchScore,
      };
    } else {
      unmatchedSuggestions.push({
        field: 'party',
        originalValue: parsedData.vendorName,
        suggestions: partyMatches,
      });
    }
  }

  // Try to infer type from line items or vendor
  let typeMatch: { id?: string; name?: string; confidence?: number } = {};
  const allDescriptions = parsedData.lineItems.map((i) => i.description).join(' ');
  const typeMatches = findBestMatches(allDescriptions || parsedData.vendorName || '', entities.types);
  if (typeMatches.length > 0 && typeMatches[0].matchScore > 0.5) {
    typeMatch = {
      id: typeMatches[0].id,
      name: typeMatches[0].name,
      confidence: typeMatches[0].matchScore,
    };
  } else if (typeMatches.length > 0) {
    unmatchedSuggestions.push({
      field: 'type',
      originalValue: allDescriptions || parsedData.vendorName || '',
      suggestions: typeMatches,
    });
  }

  // Try to match cost center
  let costCenterMatch: { id?: string; name?: string; confidence?: number } = {};
  const costCenterMatches = findBestMatches(
    allDescriptions || parsedData.vendorName || '',
    entities.costCenters.filter((cc) => !typeMatch.id || cc.typeId === typeMatch.id)
  );
  if (costCenterMatches.length > 0 && costCenterMatches[0].matchScore > 0.5) {
    costCenterMatch = {
      id: costCenterMatches[0].id,
      name: costCenterMatches[0].name,
      confidence: costCenterMatches[0].matchScore,
    };
  }

  // Try to match category
  let categoryMatch: { id?: string; name?: string; confidence?: number } = {};
  const categoryMatches = findBestMatches(
    allDescriptions || parsedData.vendorName || '',
    entities.categories.filter((cat) => !costCenterMatch.id || cat.costCenterId === costCenterMatch.id)
  );
  if (categoryMatches.length > 0 && categoryMatches[0].matchScore > 0.5) {
    categoryMatch = {
      id: categoryMatches[0].id,
      name: categoryMatches[0].name,
      confidence: categoryMatches[0].matchScore,
    };
  }

  // Infer nature
  const suggestedNature = inferNature(parsedData);

  return {
    typeId: typeMatch.id,
    typeName: typeMatch.name,
    typeConfidence: typeMatch.confidence,

    unitId: undefined, // Units typically need manual selection based on RBAC
    unitName: undefined,
    unitConfidence: undefined,

    costCenterId: costCenterMatch.id,
    costCenterName: costCenterMatch.name,
    costCenterConfidence: costCenterMatch.confidence,

    categoryId: categoryMatch.id,
    categoryName: categoryMatch.name,
    categoryConfidence: categoryMatch.confidence,

    partyId: partyMatch.id,
    partyName: partyMatch.name || parsedData.vendorName,
    partyType: partyMatch.type as 'shop' | 'vendor' | 'office' | 'labourGroup' | undefined,
    partyConfidence: partyMatch.confidence,

    suggestedNature,
    parsedData,
    hasMultipleItems: parsedData.lineItems.length > 1,
    unmatchedSuggestions,
  };
}

/**
 * Format date from various formats to YYYY-MM-DD
 */
export function formatDateForForm(dateString: string | undefined): string {
  if (!dateString) {
    return new Date().toISOString().split('T')[0];
  }

  try {
    const date = new Date(dateString);
    if (isNaN(date.getTime())) {
      return new Date().toISOString().split('T')[0];
    }
    return date.toISOString().split('T')[0];
  } catch {
    return new Date().toISOString().split('T')[0];
  }
}

/**
 * Convert parsed bill to expense form values
 */
export function convertToFormValues(
  matchedData: MatchedBillData
): Partial<{
  type: string;
  unit: string;
  costCenter: string;
  category: string;
  partyName: string;
  partyType: 'shop' | 'vendor' | 'office' | 'labourGroup';
  amount: string;
  gross: string;
  gstAmount: string;
  enableGst: boolean;
  mode: 'total' | 'unit';
  date: string;
  dueDate: string;
  invoiceNo: string;
  description: string;
  nature: string;
}> {
  const { parsedData } = matchedData;

  // Calculate total amount from line items if not provided
  let totalAmount = parsedData.totalAmount;
  if (!totalAmount && parsedData.lineItems.length > 0) {
    totalAmount = parsedData.lineItems.reduce((sum, item) => sum + (item.amount || 0), 0);
  }

  // Calculate gross (before GST)
  const gstAmount = parsedData.gstAmount || parsedData.taxAmount || 0;
  const grossAmount = totalAmount ? totalAmount - gstAmount : 0;

  // Create description from line items
  const description = parsedData.lineItems
    .map((item) => {
      let desc = item.description;
      if (item.quantity && item.unit) {
        desc += ` (${item.quantity} ${item.unit})`;
      } else if (item.quantity) {
        desc += ` (${item.quantity})`;
      }
      return desc;
    })
    .join('; ');

  return {
    type: matchedData.typeId || '',
    unit: matchedData.unitId || '',
    costCenter: matchedData.costCenterId || '',
    category: matchedData.categoryId || '',
    partyName: matchedData.partyName || '',
    partyType: matchedData.partyType || 'vendor',
    amount: totalAmount?.toString() || '',
    gross: grossAmount?.toString() || '',
    gstAmount: gstAmount?.toString() || '0',
    enableGst: gstAmount > 0,
    mode: 'total',
    date: formatDateForForm(parsedData.invoiceDate),
    dueDate: parsedData.dueDate || '',
    invoiceNo: parsedData.invoiceNumber || '',
    description: description || parsedData.notes || '',
    nature: matchedData.suggestedNature || 'routine',
  };
}
